import type { LabelInValue, MedicalGroupList } from 'models';
import { type ComponentProps, useMemo } from 'react';
import { InfinityScrollSelect } from 'ui';
import { useSearchDebounce } from 'common/hooks';
import { useMedicalGroupsInfiniteQuery } from '../queries';

type P<T extends LabelInValue> = {
  createOption: (option: MedicalGroupList) => T;
  query?: Partial<Parameters<typeof useMedicalGroupsInfiniteQuery>[0]>;
} & Omit<ComponentProps<typeof InfinityScrollSelect<T>>, 'handleRequestMoreData' | 'hasMoreData'>;

const MedicalGroupPicker = <T extends LabelInValue>({ createOption, query, ...selectProperties }: P<T>) => {
  const { isDebouncing, searchDebounced, setSearch } = useSearchDebounce({
    initialValue: query?.filter?.search,
  });

  const {
    data: medicalGroups,
    fetchNextPage: fetchNextPageOfMedicalGroups,
    hasNextPage: hasMoreMedicalGroups,
    isFetching: isFetchingMedicalGroups,
    isRefetching,
  } = useMedicalGroupsInfiniteQuery({
    ...query,
    filter: { ...query?.filter, search: searchDebounced },
    pagination: {
      pageSize: 10,
      ...query?.pagination,
    },
  });

  const allFetchedMedicalGroups = useMemo(
    () => medicalGroups?.pages.flatMap((page) => page.data.map((option) => createOption(option))),
    [medicalGroups, createOption],
  );

  return (
    <InfinityScrollSelect<T>
      allowClear
      handleRequestMoreData={fetchNextPageOfMedicalGroups}
      hasMoreData={hasMoreMedicalGroups}
      isRefetching={isRefetching}
      loading={isFetchingMedicalGroups || isDebouncing}
      onSearch={setSearch}
      options={allFetchedMedicalGroups}
      placeholder="Choose medical group..."
      {...selectProperties}
    />
  );
};

export default MedicalGroupPicker;
